import React, { FC, useEffect, useState } from 'react';
import { Avatar, Badge } from '@mui/material';
import { styled } from '@mui/material/styles';
import { StacklineTheme, useStacklineTheme } from '@stackline/ui';
import { useAppSelector, useUserProfileImage } from 'src/utils/Hooks';
import _get from 'lodash/get';

export interface AppProfileIconProps {
  userId?: string;
  size?: number;
  initials?: string;
  fontSize?: string;
  shouldShowNotificationBadge?: boolean;
  backgroundColor?: string;
  imageSrc?: string;
  customIcon?: React.ReactElement;
}

const StyledAvatar = styled(Badge)((props: { theme: StacklineTheme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: props.theme.colors.primary,
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    transform: 'translate(7px, -5px)'
  }
}));

/**
 * - renders user profile icon, defaulted to table icon width & height
 * - displays placeholder icon for no imageUrl
 */
const AppProfileIcon: FC<AppProfileIconProps> = ({
  size = 30,
  shouldShowNotificationBadge = false,
  userId,
  initials,
  fontSize,
  backgroundColor,
  imageSrc,
  customIcon
}) => {
  const theme = useStacklineTheme();
  const user = useAppSelector((state) => state.user);
  const { firstName, lastName } = user.config;
  const userInitials = initials || `${_get(firstName, 0, '')}${_get(lastName, 0, '')}`;

  const userProfileImage = useUserProfileImage(userId);
  const [imageLoaded, setImageLoaded] = useState(false);

  const defaultBackgroundColor = backgroundColor || theme.colors.primary;

  useEffect(() => {
    // Skip image loading check if we have a custom icon
    if (customIcon) {
      setImageLoaded(true);
      return;
    }
    
    const img = new Image();
    img.src = imageSrc || userProfileImage;
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageLoaded(false);
  }, [imageSrc, userProfileImage, customIcon]);

  return (
    <>
      <StyledAvatar
        theme={theme as any}
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        variant={shouldShowNotificationBadge ? 'dot' : 'standard'}
      >
        <Avatar
          sx={{
            width: size,
            height: size,
            bgcolor: customIcon || imageLoaded ? 'transparent' : defaultBackgroundColor
          }}
          alt=" "
          src={customIcon ? undefined : (imageSrc || userProfileImage)}
        >
          {customIcon ? (
            React.cloneElement(customIcon, { 
              style: { 
                width: size,
                height: size 
              } 
            })
          ) : (
            <span style={{ fontSize: fontSize || '14px' }}>
              {`${_get(userInitials, 0, '').toUpperCase()}${_get(userInitials, 1, '').toUpperCase()}`}
            </span>
          )}
        </Avatar>
      </StyledAvatar>
    </>
  );
};
export default React.memo(AppProfileIcon);
